import { useTranslation } from 'react-i18next'
import {
  Card,
  CardBody,
  getContrastTextColor,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
} from '@structural-climbing/ui'
import { formatMoney } from '../products/money'
import { feeBreakdown } from './paymentFees'

export interface FeeExample {
  /** The plan's name, or null for a sample amount. */
  label: string | null
  price: number
}

interface Props {
  examples: FeeExample[]
  currency: string
  accent: string
}

const STRIPE_COLOR = '#635BFF'
const PLATFORM_COLOR = '#1f2937'

function Dot({ color }: { color: string }) {
  return <div className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: color }} />
}

/**
 * Several real payments, split three ways, so an owner sees exactly what
 * reaches their bank before connecting anything.
 *
 * The bar is mostly the gym's colour on purpose: that is the honest picture.
 * Stripe and Structural together are a few percent, and the cap means the
 * platform's slice shrinks as the plan gets bigger.
 */
export function FeeExamples({ examples, currency, accent }: Props) {
  const { t, i18n } = useTranslation()
  // "97,6%" in Romanian, "97.6%" in English.
  const pct = (n: number) =>
    new Intl.NumberFormat(i18n.language.startsWith('ro') ? 'ro-RO' : 'en-GB', {
      style: 'percent',
      maximumFractionDigits: 1,
    }).format(n / 100)
  const rows = examples.map((e) => ({ ...e, fees: feeBreakdown(e.price) }))
  const lei = (n: number) => formatMoney(n, currency)

  const keeps = rows.map((r) => r.fees.keepPercent)
  const lowest = Math.min(...keeps)
  const highest = Math.max(...keeps)

  return (
    <div className="flex flex-col gap-4">
      <Card shadow="none" style={{ backgroundColor: accent, color: getContrastTextColor(accent) }}>
        <CardBody className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm font-medium opacity-90">{t('payments.examplesHeadline')}</p>
          <p className="text-2xl font-bold tabular-nums">
            {lowest === highest ? pct(lowest) : `${pct(lowest)}–${pct(highest)}`}
          </p>
        </CardBody>
      </Card>

      <Table removeWrapper aria-label={t('payments.examplesTitle')}>
        <TableHeader>
          <TableColumn>{t('payments.examplesColPlan')}</TableColumn>
          <TableColumn align="end">{t('payments.examplesColYou')}</TableColumn>
        </TableHeader>
        <TableBody>
          {rows.map(({ label, price, fees }) => (
            <TableRow key={`${label}-${price}`}>
              <TableCell className="w-full">
                <div className="flex flex-col gap-2 py-1">
                  <div>
                    <p className="text-sm font-medium">{label ?? lei(price)}</p>
                    <p className="text-xs text-foreground/50">
                      {t('payments.examplesMemberPays', { amount: lei(price) })}
                    </p>
                  </div>
                  <div
                    className="flex h-2.5 w-full overflow-hidden rounded-full bg-default-100"
                    aria-hidden="true"
                  >
                    <div
                      style={{ width: `${(fees.net / price) * 100}%`, backgroundColor: accent }}
                    />
                    <div
                      style={{
                        width: `${(fees.stripe / price) * 100}%`,
                        backgroundColor: STRIPE_COLOR,
                      }}
                    />
                    <div
                      style={{
                        width: `${(fees.platform / price) * 100}%`,
                        backgroundColor: PLATFORM_COLOR,
                      }}
                    />
                  </div>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-foreground/60 tabular-nums">
                    <div className="flex items-center gap-1.5">
                      <Dot color={accent} />
                      {t('payments.examplesYou', { amount: lei(fees.net) })}
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Dot color={STRIPE_COLOR} />
                      {t('payments.examplesStripe', { amount: lei(fees.stripe) })}
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Dot color={PLATFORM_COLOR} />
                      {t('payments.examplesPlatform', { amount: lei(fees.platform) })}
                    </div>
                  </div>
                </div>
              </TableCell>
              <TableCell className="align-top">
                <div className="flex flex-col items-end py-1">
                  <p className="whitespace-nowrap text-base font-semibold tabular-nums">
                    {lei(fees.net)}
                  </p>
                  <p className="whitespace-nowrap text-xs text-foreground/50">
                    {t('payments.examplesYouKeep', { percent: pct(fees.keepPercent) })}
                  </p>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <p className="text-xs text-foreground/50">{t('payments.examplesFootnote')}</p>
    </div>
  )
}
